import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import { resolveProviderLandingRoute } from '@/composables/useProviderLanding'
import { useUserStore } from '@/stores/user'

/**
 * Hospital-neutral links for email sent to many hospitals at once:
 * `/go/iv-bulk?utm_campaign=…` opens Bulk Discover at the reader's own hospital.
 *
 * These names are a contract with email already delivered. Never remove or rename one;
 * when a page moves, point its name at the new route instead.
 */
export const GO_LINKS: Readonly<Record<string, string>> = Object.freeze({
  'iv-lookup': 'iv-lookup',
  'iv-bulk': 'iv-bulk',
  'iv-history': 'iv-history',
  'iv-inbox': 'iv-inbox',
  'iv-price-list': 'iv-price-list',
  'reports': 'provider-reports',
  'value': 'provider-value-dashboard',
})

/** Set on a go link a logged-out user is sent to log in for: they pick a hospital first. */
const PICK_KEY = 'pick'

/** Carries the go link's feature through the hospital picker. */
export const GO_FEATURE_KEY = 'go'

/** The route name a go link's feature opens, or null for anything not in the list. */
export function goLinkRouteName(feature: unknown): string | null {
  if (typeof feature !== 'string' || !Object.hasOwn(GO_LINKS, feature))
    return null

  return GO_LINKS[feature]
}

/** The link's own query, without the keys go links use internally. */
export function withoutGoKeys(query: LocationQuery): LocationQuery {
  const rest = { ...query }

  delete rest[PICK_KEY]
  delete rest[GO_FEATURE_KEY]

  return rest
}

/**
 * The URL to send a logged-out user back to after login. A go link gets `pick=1`, so that
 * someone at several hospitals chooses one, as they would after any other login, instead
 * of landing in whichever hospital they used last. It's part of the URL so it survives
 * every login path, including a magic link opened in a new tab.
 */
export function loginReturnPath(fullPath: string): string {
  if (!fullPath.startsWith('/go/'))
    return fullPath

  const url = new URL(fullPath, 'http://app.invalid')

  url.searchParams.set(PICK_KEY, '1')

  return url.pathname + url.search + url.hash
}

/**
 * Where `/go/:feature` sends a logged-in user. Runs as the route's `beforeEnter`, after the
 * global guard has loaded the user and their hospitals.
 */
export function resolveGoLink(to: RouteLocationNormalized): RouteLocationRaw {
  const user = useUserStore()
  const feature = to.params.feature
  const routeName = goLinkRouteName(feature)
  const query = withoutGoKeys(to.query)

  if (user.data?.access?.vector)
    return { name: 'vector-home', query }

  // The normal post-login page shows "account not set up" to users without IDV.
  const providerUuid = user.activeProvider?.uuid ?? Object.keys(user.data?.access?.provider ?? {})[0]
  if (!routeName || !providerUuid || !resolveProviderLandingRoute(providerUuid))
    return { name: 'dashboard-interstitial', query }

  if (user.hasMultipleProviders && (to.query[PICK_KEY] !== undefined || !user.activeProvider))
    return { name: 'dashboard-select-provider', query: { ...query, [GO_FEATURE_KEY]: feature as string } }

  return { name: routeName, params: { provider_uuid: providerUuid }, query }
}
